import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import CheckerProductsTableComponent from "../../components/tables/Checker/CheckerProductsTableComponent";
import LabelFieldComponent from "../../components/fields/LabelFieldComponent";
import PageLayout from "../../layouts/PageLayout";

export default function CheckerProductList() {
  const { t, n } = useContext(TranslatorContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active Tab: 'new' | 'checked' | 'assign_stockkeeper' | 'overall_checked' | 'rejected'
  const [activeTab, setActiveTab] = useState("new");

  const [allProducts, setAllProducts] = useState([]);
  const [productsList, setProductsList] = useState([]);

  // Filter Dropdown Options
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [brandsOptions, setBrandsOptions] = useState([]);

  // Filter State Values
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");

  const [counts, setCounts] = useState({
    new: 0,
    checked: 0,
    assign_stockkeeper: 0,
    overall_checked: 0,
    rejected: 0,
  });

  const tableHeaders = [
    "Product Code",
    "Title",
    "Category",
    "Brand",
    "Selling Style",
    "Buying Price",
    "Quantity",
    "Action",
  ];

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    },
  });

  const extractData = (res) => {
    if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  };

  // Fetch Categories & Brands from Initial Data
  const fetchInitialFilters = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${baseUrl}/checker/products/initial-data`, getAuthHeader());
      const resData = response.data?.data || response.data;

      if (resData?.product_categories) {
        const flattenedCategories = [];
        resData.product_categories.forEach((cat) => {
          if (cat.child_categories && cat.child_categories.length > 0) {
            cat.child_categories.forEach((child) => {
              flattenedCategories.push({
                id: child.id,
                name: `${cat.name} > ${child.name}`,
              });
            });
          } else {
            flattenedCategories.push({
              id: cat.id,
              name: cat.name,
            });
          }
        });
        setCategoriesOptions(flattenedCategories);
      }

      if (resData?.brands) setBrandsOptions(resData.brands);
    } catch (err) {
      console.error("Error fetching initial filter options:", err);
    }
  }, []);

  // Fetch Counts for 5 Cards
  const fetchCounts = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const [newRes, checkedRes, assignRes, overallRes, rejectedRes] = await Promise.all([
        axios.get(`${baseUrl}/checker/products`, getAuthHeader()),
        axios.get(`${baseUrl}/checker/checked-products`, getAuthHeader()),
        axios.get(`${baseUrl}/checker/assign-stockkepper-products`, getAuthHeader()),
        axios.get(`${baseUrl}/checker/overall-checked-products`, getAuthHeader()),
        axios.get(`${baseUrl}/checker/rejected-products`, getAuthHeader()),
      ]);

      setCounts({
        new: extractData(newRes).length,
        checked: extractData(checkedRes).length,
        assign_stockkeeper: extractData(assignRes).length,
        overall_checked: extractData(overallRes).length,
        rejected: extractData(rejectedRes).length,
      });
    } catch (err) {
      console.error("Error fetching checker product counts:", err);
    }
  }, []);

  // Fetch Tab Specific Data
  const fetchTabData = useCallback(async (tab) => {
    setLoading(true);
    setError(null);

    let endpoint = "";
    if (tab === "new") endpoint = "/checker/products";
    else if (tab === "checked") endpoint = "/checker/checked-products";
    else if (tab === "assign_stockkeeper") endpoint = "/checker/assign-stockkepper-products";
    else if (tab === "overall_checked") endpoint = "/checker/overall-checked-products";
    else if (tab === "rejected") endpoint = "/checker/rejected-products";

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        getAuthHeader()
      );

      const listData = extractData(response);
      setAllProducts(listData);
      setProductsList(listData);
    } catch (err) {
      console.error("Error fetching checker tab products:", err);
      setError(
        err.response?.data?.message || "Failed to fetch products. Please try again."
      );
      setAllProducts([]);
      setProductsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Read URL Tab Param on Load
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get("tab");
    if (
      tabParam &&
      ["new", "checked", "assign_stockkeeper", "overall_checked", "rejected"].includes(tabParam)
    ) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  useEffect(() => {
    fetchInitialFilters();
    fetchCounts();
  }, [fetchInitialFilters, fetchCounts]);

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  // Client Side Filter Handler (Search, Category, Brand)
  useEffect(() => {
    let filtered = [...allProducts];

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (p) =>
          (p.title && p.title.toLowerCase().includes(query)) ||
          (p.product_code && p.product_code.toLowerCase().includes(query))
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (p) => String(p.category_id || p.category?.id) === String(selectedCategory)
      );
    }

    if (selectedBrand) {
      filtered = filtered.filter(
        (p) => String(p.brand_id || p.brand?.id) === String(selectedBrand)
      );
    }

    setProductsList(filtered);
  }, [allProducts, searchTerm, selectedCategory, selectedBrand]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    navigate(`/checker-products?tab=${tabKey}`);
  };

  const cardsData = [
    {
      key: "new",
      title: "New Products",
      digit: counts.new,
      icon: "shopping_bag",
      variant: activeTab === "new" ? "lg blue active-card" : "lg blue",
    },
    {
      key: "checked",
      title: "Checked Products",
      digit: counts.checked,
      icon: "fact_check",
      variant: activeTab === "checked" ? "lg purple active-card" : "lg purple",
    },
    {
      key: "assign_stockkeeper",
      title: "Assign Stockkeeper",
      digit: counts.assign_stockkeeper,
      icon: "assignment_ind",
      variant: activeTab === "assign_stockkeeper" ? "lg yellow active-card" : "lg yellow",
    },
    {
      key: "overall_checked",
      title: "Overall Checked",
      digit: counts.overall_checked,
      icon: "verified_user",
      variant: activeTab === "overall_checked" ? "lg green active-card" : "lg green",
    },
    {
      key: "rejected",
      title: "Rejected Products",
      digit: counts.rejected,
      icon: "gpp_bad",
      variant: activeTab === "rejected" ? "lg red active-card" : "lg red",
    },
  ];

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                {t("product_list")} ({productsList.length})
              </h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("home")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("products")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">{t("product_list")}</li>
              </ul>
            </div>
          </div>
        </Col>

        {/* 5 Status Cards */}
        {cardsData.map((card) => (
          <Col key={card.key} xs={12} sm={6} md={4} className="mb-3">
            <div
              style={{ cursor: "pointer" }}
              onClick={() => handleTabChange(card.key)}
            >
              <FloatCardComponent
                variant={card.variant}
                digit={n ? n(card.digit) : card.digit}
                title={card.title}
                icon={card.icon}
              />
            </div>
          </Col>
        ))}

        <Col xl={12}>
          <div className="mc-card">
            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}

            {/* Filter Controls Bar */}
            <Row className="mb-4">
              <Col xs={12} sm={6} md={4}>
                <LabelFieldComponent
                  type="search"
                  placeholder={t("search_by_title_or_code")}
                  label={t("search")}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-3"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>

              <Col xs={12} sm={6} md={4}>
                <LabelFieldComponent
                  label={t("category_by")}
                  option={[
                    "All Categories",
                    ...categoriesOptions.map((c) => c.name),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-3"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "All Categories") {
                      setSelectedCategory("");
                      return;
                    }
                    const selected = categoriesOptions.find((c) => c.name === value);
                    setSelectedCategory(selected?.id || "");
                  }}
                />
              </Col>

              <Col xs={12} sm={6} md={4}>
                <LabelFieldComponent
                  label={t("brand_by")}
                  option={[
                    "All Brands",
                    ...brandsOptions.map((b) => b.name || b.title),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-3"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "All Brands") {
                      setSelectedBrand("");
                      return;
                    }
                    const selected = brandsOptions.find((b) => (b.name || b.title) === value);
                    setSelectedBrand(selected?.id || "");
                  }}
                />
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_products...")}</p>
                  </div>
                ) : (
                  <CheckerProductsTableComponent
                    thead={tableHeaders}
                    tbody={productsList}
                    activeTab={activeTab}
                  />
                )}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}